import { useState } from 'react'
import useKeyPress from '@/core/hooks/useKeyPress/useKeyPress'
import { AccountModule } from '@/core/modules/account/account.module'
import { isValidEmail, normalizeEmail } from '@/core/utils/email'
import { Controller, useForm } from 'react-hook-form'
import {
    NotInvitedContentWrapper,
    StyledActionButton,
    StyledTextField,
} from './notInvitedContent.styles'
import Typography from '@/ui/components/typography'

type TNewInvitationForm = {
    email: string
}

type TNotInvitedContentProps = {
    setIsInvited: (isInvited: boolean) => void
}

const INVITE_FAILED_MESSAGE = 'Something went wrong. Please try again.'

const InvitedContent = ({ setIsInvited }: TNotInvitedContentProps) => {
    const {
        control,
        formState: { errors, isValid, isDirty },
        getValues,
        setValue,
        trigger,
    } = useForm<TNewInvitationForm>({
        mode: 'onBlur',
    })
    const [isSending, setIsSending] = useState(false)
    const [submitError, setSubmitError] = useState('')

    useKeyPress(() => onSendInvite(), ['Enter'], [isValid, isSending])

    const onBlur = () => {
        setValue('email', normalizeEmail(getValues().email))
        isDirty && trigger()
    }

    const onSendInvite = async () => {
        if (!isValid || isSending) {
            return
        }

        setSubmitError('')
        setIsSending(true)

        const isInvited = await AccountModule.sendNewAccountInvitation(
            normalizeEmail(getValues().email)
        )

        setIsSending(false)

        isInvited ? setIsInvited(true) : setSubmitError(INVITE_FAILED_MESSAGE)
    }

    const errorMessage = (errors?.email?.message as string) || submitError

    return (
        <NotInvitedContentWrapper>
            <Typography
                className="title"
                variant="xxl-regular"
                noWrap={false}
                testId="not-invited-title"
            >
                Secure Your Environment with Stance!
            </Typography>
            <Typography
                className="description"
                variant="xs-regular"
                noWrap={false}
            >
                Enter your email below, and if you're an eligible At-Bay
                insured, we'll send you an invite to start using
                <a
                    href="https://www.at-bay.com/security/exposure-manager/"
                    target="_blank"
                >
                    Stance.
                </a>
            </Typography>
            <div className="form" onBlur={onBlur}>
                <Controller
                    name="email"
                    control={control}
                    rules={{
                        required: true,
                        validate: (value) =>
                            isValidEmail(value) ||
                            'Please insert a valid email address',
                    }}
                    render={({ field }) => (
                        <div>
                            <StyledTextField
                                color="primary"
                                inputType="text"
                                label="Enter email address"
                                data-testid="email"
                                textValue={field.value}
                                onChange={field.onChange}
                                error={!!errors?.email || !!submitError}
                                accessibilityId="email"
                            />
                            <Typography
                                className="error"
                                variant="xs-regular"
                                noWrap={false}
                                testId="email-error"
                            >
                                {errorMessage}
                            </Typography>
                        </div>
                    )}
                />
            </div>
            <div className="action-button-wrapper">
                <StyledActionButton
                    className="action-button"
                    dataTestId="send-button"
                    text="Get started"
                    disabled={!isValid || isSending}
                    onClick={onSendInvite}
                />
            </div>
        </NotInvitedContentWrapper>
    )
}

export default InvitedContent
